import { Fragment, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { Check, DotsSixVertical } from '@phosphor-icons/react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '@/components/ui/button';
import { getCoachGoals, setCoachGoals, MAX_COACH_GOALS, type CoachGoal } from '@/api/coach';

// GoalEditor — the reusable "what matters this season?" picker body
// (docs/ai-club-coach-plan.md §3). One intuitive step: a single drag-and-drop
// list of EVERY catalog goal. The top MAX_COACH_GOALS are highlighted — those
// are the club's goals, in priority order (top = most important). Drag a goal
// up into the highlighted zone to choose it; whatever it pushes past the cutoff
// stops being a goal. weight is derived from the order on save (first = highest
// weight), and the whole coach stack (dashboard ranking, the AI's
// get_coach_dashboard / list_coach_goals tools) reads weight. Reused by the
// onboarding card (CoachGoalsCard) and the dashboard's goal editor dialog.
// Org-admin only — the endpoint enforces it.
export function GoalEditor({
  slug,
  onSaved,
  onCancel,
}: {
  slug: string;
  onSaved?: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['coach-goals', slug],
    queryFn: () => getCoachGoals(token, slug),
    enabled: !!slug,
  });

  // order holds EVERY catalog key in one ranked list; the first FOCUS entries
  // are the chosen goals (index 0 = highest weight), the rest sit unchosen
  // below the cutoff.
  const [order, setOrder] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const FOCUS = MAX_COACH_GOALS;

  // Seed once loaded: chosen goals first (highest weight first), then every
  // other catalog goal in catalog order — unless the user is mid-edit.
  useEffect(() => {
    if (!query.data || dirty) return;
    // A stored goal_key can outlive the catalog (a goal retired between
    // releases). Drop those here: they have no title to render — the row would
    // show a raw i18n key — and keeping one inside the focus zone makes the
    // save 400 on "unknown goal key".
    const known = new Set(query.data.catalog.map((c) => c.key));
    const chosen = query.data.goals.map((g) => g.goalKey).filter((k) => known.has(k));
    const rest = query.data.catalog.map((c) => c.key).filter((k) => !chosen.includes(k));
    setOrder([...chosen, ...rest]);
  }, [query.data, dirty]);

  // Preserve any per-goal targets a chosen goal already carried, so re-ranking
  // doesn't drop them on save.
  const targetByKey = useMemo(() => {
    const m = new Map<string, { targetValue: number | undefined; targetUnit: CoachGoal['targetUnit'] }>();
    for (const g of query.data?.goals ?? []) {
      m.set(g.goalKey, { targetValue: g.targetValue, targetUnit: g.targetUnit });
    }
    return m;
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (goals: CoachGoal[]) => setCoachGoals(token, slug, goals),
    onSuccess: (res) => {
      queryClient.setQueryData(['coach-goals', slug], res);
      void queryClient.invalidateQueries({ queryKey: ['coach-dashboard', slug] });
      setDirty(false);
      onSaved?.();
    },
  });

  // Drag-to-reorder over the whole list. @dnd-kit sensors: pointer (4px so a
  // click still works), touch (long-press so a tap-scroll isn't hijacked), and
  // keyboard (focus a handle, space to lift, arrows to move) for accessibility.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrder((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return prev;
      setDirty(true);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const titleOf = (key: string) => t(`coach.goals.catalog.${key}.title`);

  const onSave = () => {
    // Only the top FOCUS are the club's goals; first = highest weight so the
    // dashboard ranks by the club's stated priority.
    const chosen = order.slice(0, FOCUS);
    const goals: CoachGoal[] = chosen.map((key, i) => {
      const tgt = targetByKey.get(key);
      return {
        goalKey: key,
        weight: FOCUS - i,
        ...(tgt?.targetValue != null ? { targetValue: tgt.targetValue } : {}),
        ...(tgt?.targetUnit ? { targetUnit: tgt.targetUnit } : {}),
      };
    });
    mutation.mutate(goals);
  };

  if (query.isLoading || order.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {t('coach.goals.wizard.focusHint', { count: FOCUS })}
      </p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <ul className="flex flex-col gap-2">
            {order.map((key, i) => (
              <Fragment key={key}>
                {i === FOCUS && (
                  <li
                    aria-hidden
                    className="flex items-center gap-3 px-1 pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    <span className="h-px flex-1 bg-border" />
                    {t('coach.goals.wizard.cutoffLabel')}
                    <span className="h-px flex-1 bg-border" />
                  </li>
                )}
                <SortableGoalRow
                  id={key}
                  title={titleOf(key)}
                  rank={i + 1}
                  chosen={i < FOCUS}
                />
              </Fragment>
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={mutation.isPending}>
            {t('coach.goals.wizard.cancel')}
          </Button>
        )}
        <Button disabled={!dirty || mutation.isPending} onClick={onSave}>
          {mutation.isPending ? t('coach.goals.wizard.cta') + '…' : t('coach.goals.wizard.cta')}
        </Button>
      </div>

      {mutation.isError && (
        <p className="text-sm text-destructive">{t('coach.goals.wizard.error')}</p>
      )}
    </div>
  );
}

// SortableGoalRow is one draggable row in the unified ranked list. Chosen rows
// (top FOCUS) are highlighted with a lime tint + a rank number + a check; rows
// below the cutoff are muted with no rank. The whole row is the drag handle
// (via the grip affordance) so dragging anywhere on it reorders; the grip and
// row meet the 44px tap-target floor.
function SortableGoalRow({
  id,
  title,
  rank,
  chosen,
}: {
  id: string;
  title: string;
  rank: number;
  chosen: boolean;
}) {
  const { t } = useTranslation('common');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={[
        'flex min-h-11 items-center gap-2 rounded-pill border px-2 py-1.5 transition-colors',
        chosen ? 'border-primary bg-primary/10' : 'border-border bg-background',
      ].join(' ')}
    >
      <button
        type="button"
        className="flex h-11 w-11 shrink-0 cursor-grab touch-none items-center justify-center rounded-pill text-foreground/40 transition hover:text-foreground/70 active:cursor-grabbing"
        aria-label={t('coach.goals.wizard.dragHandleAria', { goal: title })}
        {...attributes}
        {...listeners}
      >
        <DotsSixVertical size={18} weight="bold" aria-hidden />
      </button>
      <span
        className={[
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-pill text-xs font-bold',
          chosen ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
        ].join(' ')}
        aria-hidden
      >
        {chosen ? rank : ''}
      </span>
      <span className={['flex-1 text-sm font-semibold', chosen ? '' : 'text-foreground/70'].join(' ')}>
        {title}
      </span>
      {chosen && <Check size={16} weight="bold" className="mr-1 shrink-0 text-primary" aria-hidden />}
    </li>
  );
}
